import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal } from '../components/Reveal';
import { Img } from '../components/Img';

export function Magazine() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/magazine?lang=${lang}`).then((r) => setItems(r.data));
  }, [lang]);

  return (
    <>
      <Seo title={t('magazine.title')} description={t('magazine.lead')} path="/storie/articoli" />

      <header className="py-14 md:py-24">
        <div className="wrap">
          <h1 className="display-xl max-w-[14ch]">{t('magazine.title')}</h1>
          <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">{t('magazine.lead')}</p>
        </div>
      </header>

      <section data-testid="magazine-list" className="py-14 md:py-20">
        <div className="wrap grid gap-14 md:gap-20">
          {items.map((a, i) => (
            <Reveal key={a.slug}>
              <Link
                to={`/storie/articoli/${a.slug}`}
                data-testid={`article-${a.slug}`}
                className={`group grid items-center gap-8 md:gap-14 ${
                  i % 2 === 0 ? 'md:grid-cols-[1.1fr_1fr]' : 'md:grid-cols-[1fr_1.1fr]'
                }`}
              >
                <div
                  className={`aspect-[3/2] overflow-hidden bg-carta-2 ${
                    i % 2 === 0 ? '' : 'md:order-2'
                  }`}
                >
                  <Img
                    src={a.image}
                    alt={a.title}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    className="img-zoom h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-3xl leading-tight transition-colors group-hover:text-marsala md:text-4xl lg:text-5xl">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-[14px] text-inchiostro/70">
                    {a.kicker} · {new Date(a.date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT')}
                  </p>
                  <p className="prosa mt-5">{a.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[14px] font-bold text-marsala">
                    {t('common.readMore')}
                    <ArrowUpRight size={15} strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export function Article() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get(`/magazine/${slug}?lang=${lang}`).then((r) => setA(r.data)).catch(() => setA(null));
  }, [slug, lang]);

  if (!a) return <div className="wrap py-32 text-[14px] text-inchiostro/70">{t('common.loading')}</div>;

  return (
    <>
      <Seo
        title={a.title}
        description={a.excerpt}
        image={a.image}
        path={`/storie/articoli/${slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: a.title,
          description: a.excerpt,
          image: a.image,
          datePublished: a.date,
          publisher: { '@type': 'Organization', name: 'VdE Tivoli 1996' },
        }}
      />

      <nav aria-label="breadcrumb" className="wrap pt-6">
        <Link
          to="/storie/articoli"
          className="inline-flex min-h-[44px] items-center gap-2 text-[14px] font-bold text-inchiostro/70 transition-colors hover:text-marsala"
        >
          <ChevronLeft size={13} /> {t('magazine.title')}
        </Link>
      </nav>

      <article className="py-10 md:py-16">
        <div className="wrap">
          <h1 className="max-w-[24ch] text-4xl leading-[1.04] md:text-5xl lg:text-6xl">{a.title}</h1>
          <p className="mt-3 text-[14px] text-inchiostro/70">
            {a.kicker} · {new Date(a.date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT')}
          </p>
          <p className="prosa mt-8 max-w-[52ch] border-t-2 border-inchiostro pt-6 font-display text-xl italic md:text-2xl">
            {a.excerpt}
          </p>
        </div>

        <div className="wrap mt-12 md:mt-16">
          <div className="aspect-[16/9] overflow-hidden bg-carta-2">
            <Img src={a.image} alt={a.title} sizes="100vw" className="h-full w-full object-cover" />
          </div>
        </div>

        <div className="wrap mt-12 md:mt-16">
          <div className="max-w-[68ch] space-y-7">
            {a.body?.map((p, i) => (
              <p
                key={i}
                className={`text-lg leading-[1.75] text-inchiostro/85 ${
                  i === 0
                    ? 'first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:text-[clamp(2.8rem,9vw,4.2rem)] first-letter:leading-[0.82] first-letter:text-marsala'
                    : ''
                }`}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </article>
    </>
  );
}
